import {
  SearchOutlined,
  ReloadOutlined,
  ClockCircleOutlined
} from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  Typography,
  Row,
  Col,
  Card,
  Input,
  Button,
  Spin,
  Empty,
  Statistic,
  Select,
  DatePicker,
  Pagination
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState, useMemo } from "react";

import Layout from "../components/common/Layout";

import { useUser } from "@/contexts/UserContext";
import { useProjects } from "@/hooks/businessCentral";
import {
  IAttendanceWorker,
  useGetSites,
  CHIME_QUERY_KEYS,
  useGetSiteAttendance,
  IAttendanceStatus
} from "@/hooks/chime";
import { ISelectOption } from "@/types";
import storage from "@/utils/storage";

const { Text } = Typography;

type FilterOption = IAttendanceStatus | "All";

const PAGE_SIZE = 50;

const WhosOnSitePage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<FilterOption>("All");
  const [selectedSite, setSelectedSite] = useState<ISelectOption | null>(null);
  const [selectedAttendanceDate, setSelectedAttendanceDate] =
    useState<Dayjs | null>(() => dayjs());
  const [currentPage, setCurrentPage] = useState(1);

  const user = useUser();
  const queryClient = useQueryClient();

  const { isLoading: projectsLoading, data: projectsData } = useProjects();
  const { isLoading: sitesLoading, data: sitesData } = useGetSites();
  const {
    isLoading: siteAttendanceLoading,
    isFetching: siteAttendanceFetching,
    data: siteAttendanceData
  } = useGetSiteAttendance(
    (selectedSite?.description as string) || null,
    selectedAttendanceDate ? selectedAttendanceDate.format("YYYY-MM-DD") : null
  );

  const projectOptions = useMemo(() => {
    if (!user?.regionCode || !projectsData || projectsData.value.length === 0)
      return [];

    const options: ISelectOption[] = [];
    const savedProjectNo = storage.getProject()?.split("~~")?.[0];

    // Build a map from project reference (no) to site id
    const referenceToSiteId = new Map<string, string>();
    sitesData?.forEach((site) => {
      if (site.reference) {
        referenceToSiteId.set(site.reference, site.id);
      }
    });

    projectsData.value.forEach((project) => {
      if (
        project.no &&
        project.jobPostingGroup &&
        project.jobPostingGroup === user.regionCode
      ) {
        const siteId = referenceToSiteId.get(project.no) || "";
        options.push({
          value: project.no,
          label: project.no + " - " + project.description,
          description: siteId
        });

        if (savedProjectNo && savedProjectNo === project.no) {
          setSelectedSite({
            value: project.no,
            label: project.no + " - " + project.description,
            description: siteId
          });
        }
      }
    });

    return options;
  }, [user, sitesData, projectsData]);

  const selectedSiteHasNoSiteId =
    selectedSite !== null && !selectedSite.description;

  // Filter personnel based on search term and status filter
  const filteredWorkers = useMemo(() => {
    if (!siteAttendanceData?.workers || siteAttendanceData.workers.length === 0)
      return [];

    let result = siteAttendanceData.workers;

    // Apply status filter
    if (filter !== "All") {
      const statusMap: Record<FilterOption, IAttendanceStatus | null> = {
        All: null,
        Active: "Active",
        Break: "Break",
        Departed: "Departed"
      };
      const targetStatus = statusMap[filter];
      if (targetStatus) {
        result = result.filter((p) => p.status === targetStatus);
      }
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p?.fullName?.toLowerCase()?.includes(lowerSearch) ||
          p?.role?.toLowerCase()?.includes(lowerSearch)
      );
    }

    return result;
  }, [siteAttendanceData, searchTerm, filter]);

  // Reset to first page when filters change
  const handleFilterChange = (newFilter: FilterOption) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Paginate the filtered workers
  const paginatedWorkers = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredWorkers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredWorkers, currentPage]);

  // Refresh handler
  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: [CHIME_QUERY_KEYS.getSiteAttendance]
    });
  };

  const filterButtons: { label: string; value: FilterOption }[] = [
    {
      label: "All",
      value: "All"
    },
    {
      label: "Active",
      value: "Active"
    },
    {
      label: "Break",
      value: "Break"
    },
    {
      label: "Departed",
      value: "Departed"
    }
  ];

  return (
    <Layout>
      {/* Header */}
      <PageHeader
        title="Who's On Site"
        extra={
          <Button
            icon={<ReloadOutlined spin={siteAttendanceFetching} />}
            onClick={handleRefresh}
            loading={siteAttendanceFetching}
            disabled={!selectedSite || selectedSiteHasNoSiteId}
          >
            Refresh
          </Button>
        }
      />

      {/* Site & Date Selection */}
      <div className="mt-4 p-4 bg-white rounded-lg flex items-center flex-wrap gap-4">
        <Select
          showSearch
          size="large"
          options={projectOptions}
          placeholder={
            sitesLoading || projectsLoading
              ? "Loading projects..."
              : "Search by project name..."
          }
          value={selectedSite}
          onSelect={(_, obj) => {
            setSelectedSite(obj);
          }}
          disabled={sitesLoading || projectsLoading}
          filterOption={(inputValue, option: any) => {
            if (!inputValue) return true;
            return (
              option?.label?.toLowerCase().includes(inputValue.toLowerCase()) ??
              false
            );
          }}
          style={{ width: "100%", maxWidth: 400 }}
        />
        <DatePicker
          size="large"
          format="DD/MM/YYYY"
          allowClear={false}
          placeholder="Select attendance date"
          value={selectedAttendanceDate}
          onChange={setSelectedAttendanceDate}
          disabled={!selectedSite || selectedSiteHasNoSiteId}
          disabledDate={(date) => date.isAfter(dayjs())}
          style={{ width: "100%", maxWidth: 300 }}
        />
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} className="my-4">
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total"
              value={siteAttendanceData?.totalToday || 0}
              loading={siteAttendanceLoading}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderColor: "#b7eb8f" }}>
            <Statistic
              title="Active"
              value={siteAttendanceData?.activeCount || 0}
              valueStyle={{ color: "#52c41a" }}
              loading={siteAttendanceLoading}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderColor: "#ffe58f" }}>
            <Statistic
              title="On Break"
              value={siteAttendanceData?.onBreakCount || 0}
              valueStyle={{ color: "#faad14" }}
              loading={siteAttendanceLoading}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Departed"
              value={siteAttendanceData?.departedCount || 0}
              valueStyle={{ color: "#8c8c8c" }}
              loading={siteAttendanceLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <Row gutter={[16, 16]} className="mb-4 items-center">
        <Col xs={24} md={12}>
          <Input
            placeholder="Search by name or role..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={handleSearchChange}
            allowClear
            size="large"
          />
        </Col>
        <Col xs={24} md={12}>
          <div className="flex gap-2 flex-wrap">
            {filterButtons.map((button) => (
              <Button
                key={button.value}
                type={filter === button.value ? "primary" : "default"}
                onClick={() => handleFilterChange(button.value)}
              >
                {button.label}
              </Button>
            ))}
          </div>
        </Col>
      </Row>

      {selectedSiteHasNoSiteId ? (
        <div className="py-20">
          <Empty description="No records found!" />
        </div>
      ) : siteAttendanceLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" />
        </div>
      ) : siteAttendanceData && siteAttendanceData.workers?.length === 0 ? (
        <div className="py-20">
          <Empty description="No records found!" />
        </div>
      ) : null}

      {siteAttendanceData && siteAttendanceData.workers?.length > 0 && (
        <>
          {/* Workers List */}
          {filteredWorkers?.length > 0 ? (
            <Row gutter={[16, 16]}>
              {paginatedWorkers?.map((worker, index) => {
                return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={index}>
                    <WorkerCard worker={worker} />
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="py-20">
              <Empty description="No records found!" />
            </div>
          )}

          {/* Pagination */}
          {filteredWorkers.length > PAGE_SIZE && (
            <div className="flex justify-end mt-6">
              <Pagination
                current={currentPage}
                pageSize={PAGE_SIZE}
                total={filteredWorkers.length}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} workers`
                }
              />
            </div>
          )}
        </>
      )}
    </Layout>
  );
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "Active":
      return (
        <div className="flex items-center gap-x-2 px-3 py-1 rounded-full bg-green-100">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="font-medium text-green-800">{status}</span>
        </div>
      );
    case "Break":
      return (
        <div className="flex items-center gap-x-2 px-3 py-1 rounded-full bg-yellow-100">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="font-medium text-yellow-800">{status}</span>
        </div>
      );
    case "Departed":
      return (
        <div className="flex items-center gap-x-2 px-3 py-1 rounded-full bg-gray-100">
          <div className="w-2 h-2 rounded-full bg-gray-500" />
          <span className="font-medium text-gray-800">{status}</span>
        </div>
      );
    default:
      return null;
  }
};

const WorkerCard = ({ worker }: { worker: IAttendanceWorker }) => {
  return (
    <Card
      size="small"
      title={
        <div className="flex justify-between items-center py-1.5">
          <div>
            <Text strong>{worker.fullName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {worker.role}
            </Text>
          </div>
          {getStatusBadge(worker.status)}
        </div>
      }
    >
      <div className="flex justify-between bg-gray-50 rounded-lg p-3">
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Check-In
          </Text>
          <div className="flex items-center gap-1 mt-1">
            <ClockCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
            <Text strong>
              {worker.checkInTime
                ? dayjs(worker.checkInTime).format("DD/MM/YYYY hh:mm A")
                : "-"}
            </Text>
          </div>
        </div>
        <div className="w-px bg-gray-200 mx-3" />
        {worker?.actualDeparture ? (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Departure
            </Text>
            <div className="flex items-center gap-1 mt-1">
              <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
              <Text type="secondary">
                {dayjs(worker.actualDeparture).format("DD/MM/YYYY hh:mm A")}
              </Text>
            </div>
          </div>
        ) : (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Expected Departure
            </Text>
            <div className="flex items-center gap-1 mt-1">
              <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
              <Text
                style={{
                  fontStyle: "italic"
                }}
                type="secondary"
              >
                {worker.expectedDeparture
                  ? dayjs(worker.expectedDeparture).format("DD/MM/YYYY hh:mm A")
                  : "-"}
              </Text>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default WhosOnSitePage;
